import React, { useEffect, useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Form, Formik, FormikProps } from 'formik'
import { Button, Box, Typography } from '@mui/material'
import { ArrowForward } from '@mui/icons-material'

import InputFormikField from '@/views/components/input-formik-field/index.component'
import routes from '@/routes'
import { colors } from '@/theme/colors.theme'

import { TwoFactorStatus } from '@/enums/two-factor-status.enum'
import InputFormikPassword from '@/views/components/input-formik-password/index.component'
import logoLunaris from '@/assets/img/logo-lunaris.png'

import TwoFactorComponent from './two-factor.component'
import {
  FormValuesLogin,
  initialValuesLogin,
  validationSchemaLogin,
} from '../hooks/schema'
import useLogin from '../hooks/use-login-form.hook'

const LoginFormComponent = () => {
  const { t } = useTranslation()

  const { twoFactorStatus, handleOnSubmitForm, setTwoFactorStatus } = useLogin()

  const formRef = useRef<FormikProps<FormValuesLogin>>(null)

  const [params] = useSearchParams()

  useEffect(() => {
    if (params.get('email')) {
      const email = params.get('email')
      const password = params.get('password')
      formRef.current.setFieldValue('email', email)
      formRef.current.setFieldValue('password', password)
    }
  }, [params])

  return (
    <Box
      sx={{
        padding: '80px 0',
        width: '100%',
        maxWidth: '450px',
        margin: '0px auto',
      }}
    >
      <Box display="flex" justifyContent="center">
        <Link to={routes.auth.LOGIN}>
          <img src={logoLunaris} alt="Logo Lunaris" />
        </Link>
      </Box>
      <Box mt="150px" mb="20px">
        <Box sx={{ display: 'inline-flex' }}>
          <Typography variant="h1" sx={{ textWrap: 'nowrap', fontSize: 24 }}>
            {t('login.welcomeToCasinoBackOffice')}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              pl: 1,
              color: colors.primary,
              fontSize: 24,
            }}
          >
            {t('login.casinoBackOffice')}
          </Typography>
        </Box>
      </Box>
      <Formik
        innerRef={formRef}
        initialValues={initialValuesLogin}
        validationSchema={validationSchemaLogin}
        onSubmit={(values) => {
          handleOnSubmitForm(values)
        }}
      >
        {({ isValid, dirty, handleSubmit, handleChange }) => (
          <Form>
            {twoFactorStatus !== TwoFactorStatus.ACTIVE &&
            twoFactorStatus !== TwoFactorStatus.ERROR ? (
              <>
                <Box>
                  <InputFormikField
                    label={t('login.usernameOrEmail')}
                    name="email"
                    autoFocus
                  />
                </Box>
                <Box my={2}>
                  <InputFormikPassword
                    label={t('labels.password')}
                    name="password"
                    onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Enter' && dirty && isValid) {
                        handleSubmit()
                      }
                    }}
                  />
                </Box>
                <Box mb={2} width="100%">
                  <Button
                    disabled={!(dirty && isValid)}
                    variant="contained"
                    color="primary"
                    fullWidth
                    type="submit"
                    endIcon={<ArrowForward />}
                    sx={{ py: 1 }}
                  >
                    {t('login.login')}
                  </Button>
                </Box>
              </>
            ) : (
              <TwoFactorComponent
                twoFactorStatus={twoFactorStatus}
                setTwoFactorStatus={setTwoFactorStatus}
                handleChange={handleChange}
              />
            )}
          </Form>
        )}
      </Formik>
    </Box>
  )
}

export default LoginFormComponent
